import { apiFetch, getAuthHeaders } from "@/lib/api/client";
import { apiUrl } from "@/lib/api-config";
import type {
  CreateDraftMatterInput,
  DraftClauseAlternative,
  DraftCopilotHistoryMessage,
  DraftCopilotResponse,
  DraftExportResult,
  DraftLegalRole,
  DraftMatter,
  DraftMatterDetail,
  DraftParty,
  DraftReviewFinding,
  DraftReviewGenerateResponse,
  DraftSourceDocument,
  DraftTemplate,
  UpdateDraftMatterInput,
} from "@/types/drafting";

export function fetchDraftMatters(): Promise<DraftMatter[]> {
  return apiFetch("/api/drafts");
}

export function fetchDraftMatter(id: string): Promise<DraftMatterDetail> {
  return apiFetch(`/api/drafts/${id}`);
}

export function createDraftMatter(input: CreateDraftMatterInput): Promise<DraftMatter> {
  return apiFetch("/api/drafts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDraftMatter(id: string, input: UpdateDraftMatterInput): Promise<DraftMatter> {
  return apiFetch(`/api/drafts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteDraftMatter(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/api/drafts/${id}`, { method: "DELETE" });
}

export function fetchDraftTemplates(): Promise<DraftTemplate[]> {
  return apiFetch("/api/draft-templates");
}

export function createDraftTemplate(input: {
  name: string;
  value: string;
  blurb?: string;
  version?: string;
  structure?: unknown;
}): Promise<DraftTemplate> {
  return apiFetch("/api/draft-templates", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDraftTemplate(
  id: string,
  input: Partial<{ name: string; value: string; blurb: string; version: string; structure: unknown }>
): Promise<DraftTemplate> {
  return apiFetch(`/api/draft-templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteDraftTemplate(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/api/draft-templates/${id}`, { method: "DELETE" });
}

export function fetchDraftLegalRoles(): Promise<DraftLegalRole[]> {
  return apiFetch("/api/draft-legal-roles");
}

export function createDraftLegalRole(input: {
  name: string;
  role_key: string;
  instructions: string;
}): Promise<DraftLegalRole> {
  return apiFetch("/api/draft-legal-roles", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDraftLegalRole(
  id: string,
  input: Partial<{ name: string; role_key: string; instructions: string }>
): Promise<DraftLegalRole> {
  return apiFetch(`/api/draft-legal-roles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteDraftLegalRole(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/api/draft-legal-roles/${id}`, { method: "DELETE" });
}

export async function upsertDraftLegalRole(input: {
  id?: string;
  name: string;
  role_key: string;
  instructions: string;
}): Promise<DraftLegalRole> {
  if (input.id) {
    return updateDraftLegalRole(input.id, input);
  }

  const roles = await fetchDraftLegalRoles();
  const existing = roles.find((role) => role.role_key === input.role_key && !role.is_system);
  if (existing) {
    return updateDraftLegalRole(existing.id, input);
  }

  return createDraftLegalRole(input);
}

export function extractDraftParties(
  matterId: string,
  body: {
    pasted_text?: string;
    document_text?: string;
    background_info?: string;
  }
): Promise<DraftParty[]> {
  return apiFetch(`/api/drafts/${matterId}/extract-parties`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function confirmDraftParties(
  matterId: string,
  parties: Array<Record<string, unknown>>
): Promise<{ parties: DraftParty[]; all_confirmed: boolean }> {
  return apiFetch(`/api/drafts/${matterId}/parties`, {
    method: "PUT",
    body: JSON.stringify({ parties }),
  });
}

export function uploadDraftSourceDocuments(
  matterId: string,
  documents: Array<{ fileName: string; docType: string; fileBase64: string }>
): Promise<{ source_documents: DraftSourceDocument[] }> {
  return apiFetch(`/api/drafts/${matterId}/source-documents`, {
    method: "POST",
    body: JSON.stringify({ documents }),
  });
}

export function deleteDraftSourceDocument(
  matterId: string,
  docId: string
): Promise<{ success: boolean }> {
  return apiFetch(`/api/drafts/${matterId}/source-documents/${docId}`, {
    method: "DELETE",
  });
}

export function resolveDraftConflict(
  matterId: string,
  conflictId: string,
  resolvedValue: string
): Promise<unknown> {
  return apiFetch(`/api/drafts/${matterId}/conflicts/${conflictId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ resolved_value: resolvedValue }),
  });
}

export function fetchDraftClauseLibrary(templateId: string): Promise<DraftClauseAlternative[]> {
  return apiFetch(`/api/draft-clause-alternatives?template_id=${encodeURIComponent(templateId)}`);
}

export function createDraftClauseAlternative(input: {
  template_id: string;
  section_key: string;
  clause_category?: string;
  language: string;
  posture?: string;
  jurisdiction_applicability?: string;
  approval_status?: string;
  version?: string;
}): Promise<DraftClauseAlternative> {
  return apiFetch("/api/draft-clause-alternatives", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDraftClauseAlternative(
  id: string,
  input: Partial<{
    section_key: string;
    clause_category: string;
    language: string;
    posture: string;
    jurisdiction_applicability: string;
    approval_status: string;
    version: string;
  }>
): Promise<DraftClauseAlternative> {
  return apiFetch(`/api/draft-clause-alternatives/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteDraftClauseAlternative(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/api/draft-clause-alternatives/${id}`, { method: "DELETE" });
}

export function fetchDraftClauseAlternatives(matterId: string): Promise<DraftClauseAlternative[]> {
  return apiFetch(`/api/drafts/${matterId}/clauses`);
}

export function applyDraftClauseAlternative(
  matterId: string,
  alternativeId: string,
  sectionKey?: string
): Promise<{ version_id: string; section_key: string; content_json: unknown }> {
  return apiFetch(`/api/drafts/${matterId}/clauses/${alternativeId}/apply`, {
    method: "POST",
    body: JSON.stringify(sectionKey ? { section_key: sectionKey } : {}),
  });
}

export function fetchDraftCopilotHistory(
  matterId: string
): Promise<DraftCopilotHistoryMessage[]> {
  return apiFetch(`/api/drafts/${matterId}/copilot/history`);
}

export function requestDraftCopilot(
  matterId: string,
  instruction: string,
  apply = false,
  messageId?: string
): Promise<DraftCopilotResponse> {
  return apiFetch(`/api/drafts/${matterId}/copilot`, {
    method: "POST",
    body: JSON.stringify({
      instruction,
      apply,
      ...(messageId ? { message_id: messageId } : {}),
    }),
  });
}

export function fetchDraftFindings(matterId: string): Promise<DraftReviewFinding[]> {
  return apiFetch(`/api/drafts/${matterId}/findings`);
}

export function generateDraftReview(matterId: string): Promise<DraftReviewGenerateResponse> {
  return apiFetch(`/api/drafts/${matterId}/review`, {
    method: "POST",
  });
}

export function updateDraftFinding(
  matterId: string,
  findingId: string,
  body: { status?: string; resolution_note?: string }
): Promise<DraftReviewFinding> {
  return apiFetch(`/api/drafts/${matterId}/findings/${findingId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function exportDraftDocument(
  matterId: string,
  format: "docx" | "redline" | "pdf" | "summary"
): Promise<DraftExportResult> {
  return apiFetch(`/api/drafts/${matterId}/export/${format}`);
}

function parseContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* ignore */
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  return quoted?.[1] ?? null;
}

/** Stream export through the API (avoids browser → MinIO presigned URL issues). */
export async function downloadDraftDocument(
  matterId: string,
  format: "docx" | "redline" | "pdf" | "summary"
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    apiUrl(`/api/drafts/${matterId}/export/${format}?download=1`),
    {
      credentials: "include",
      headers: authHeaders,
    }
  );

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Export failed (${response.status})`);
  }

  const blob = await response.blob();
  const fileName =
    parseContentDispositionFileName(response.headers.get("Content-Disposition")) ??
    `draft-export.${format}`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
